import React, { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiPost } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { RegisterRequest, MessageResponse, LoginResponse } from '../types/api';
import { trackSignUp } from '../utils/analytics';
import AlertMessage from '../components/AlertMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import ReportProblemLink from '../components/ReportProblemLink';
import './RegisterPage.css';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{10,}$/;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Invitation-based registration (vault invite or Forever Free invite)
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [foreverFreeToken, setForeverFreeToken] = useState<string | null>(null);
  const [emailLocked, setEmailLocked] = useState(false);

  useEffect(() => {
    // Vault invitation (sessionStorage)
    const rawInvite = sessionStorage.getItem('pendingInvite');
    if (rawInvite) {
      try {
        const pending = JSON.parse(rawInvite);
        if (pending.email && pending.token) {
          setEmail(pending.email);
          setInvitationToken(pending.token);
          setEmailLocked(true);
          return;
        }
      } catch {
        // ignore malformed data
      }
    }

    // Forever Free invitation (localStorage — survives tab changes from email confirmation)
    const rawFF = localStorage.getItem('pendingForeverFreeAccept');
    if (rawFF) {
      try {
        const pending = JSON.parse(rawFF);
        if (pending.token && pending.email) {
          setEmail(pending.email);
          setForeverFreeToken(pending.token);
          setEmailLocked(true);
        }
      } catch {
        // ignore malformed data
      }
    }
  }, []);

  function validate(): string | null {
    if (!PASSWORD_REGEX.test(password)) {
      return 'Password must be at least 10 characters with uppercase, lowercase, digit, and special character.';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match.';
    }
    return null;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const body: RegisterRequest = { firstName, lastName, email, password, confirmPassword };
      if (invitationToken) {
        body.invitationToken = invitationToken;
      }
      if (foreverFreeToken) {
        body.foreverFreeToken = foreverFreeToken;
      }
      const data = await apiPost<MessageResponse | LoginResponse>('/api/auth/register', body);

      // Forever Free registrations return a LoginResponse (auto-login — skip the login page)
      if (foreverFreeToken && 'accessToken' in data && data.accessToken) {
        trackSignUp('forever_free');
        login(data as LoginResponse);
        navigate('/');
        return;
      }

      trackSignUp(invitationToken ? 'vault_invite' : 'self_serve');
      setSuccess((data as MessageResponse).message);

      // Vault-invited registrations redirect to login after a brief pause
      if (invitationToken) {
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    // Invited users: simpler success message (no email confirmation needed)
    if (invitationToken || foreverFreeToken) {
      return (
        <div className="auth-page">
          <div className="auth-container">
            <h1 className="auth-title">Cedar Vault</h1>
            <p className="auth-subtitle">Account Created</p>
            <div className="divider" />
            <AlertMessage type="success" message={success} />
            <p className="confirm-instructions">
              Redirecting you to sign in...
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Cedar Vault</h1>
          <p className="auth-subtitle">Almost there!</p>
          <div className="divider" />
          <AlertMessage type="success" message={success} />
          <p className="confirm-instructions">
            We sent a confirmation link to <strong>{email}</strong>.
            Open that email and click the link to activate your account.
          </p>
          <p className="confirm-hint">
            Don't see it? Check your spam or junk folder.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1 className="auth-title">Cedar Vault</h1>
        <p className="auth-subtitle">
          {foreverFreeToken ? 'Create your account to claim your Forever Free vault' :
           emailLocked ? 'Create your account to accept the invitation' :
           'Create your account'}
        </p>
        <div className="divider" />

        {!emailLocked && (
          <div className="trial-callout">
            <p><strong>14-day free trial</strong> — risk free, no credit card required.</p>
          </div>
        )}

        <AlertMessage type="error" message={error} />

        <form onSubmit={handleSubmit}>
          <div className="register-name-row">
            <div className="form-group">
              <label className="form-label" htmlFor="firstName">First name</label>
              <input
                id="firstName"
                className="form-input"
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="lastName">Last name</label>
              <input
                id="lastName"
                className="form-input"
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          {emailLocked ? (
            <div className="form-group">
              <label className="form-label">Email</label>
              <p className="register-email-locked">{email}</p>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label" htmlFor="regEmail">Email</label>
              <input
                id="regEmail"
                className="form-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="regPassword">Password</label>
            <input
              id="regPassword"
              className="form-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
            <p className="password-hint">
              At least 10 characters, with uppercase, lowercase, digit, and special character.
            </p>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="regConfirm">Confirm password</label>
            <input
              id="regConfirm"
              className="form-input"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>

          <button className="btn-primary" type="submit" disabled={loading}>
            {loading ? <LoadingSpinner small /> : 'Create Account'}
          </button>
          <p className="privacy-notice">
            By creating an account, you agree to our{' '}
            <a href="https://cedarvaultapp.com/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>.
          </p>
        </form>

        <div className="auth-links">
          <Link to="/login">Already have an account? Sign in</Link>
        </div>
        <p className="auth-site-link">
          <Link to="/help">Need help?</Link>
          &ensp;&middot;&ensp;
          <a href="https://cedarvaultapp.com" target="_blank" rel="noopener noreferrer">
            About Cedar Vault
          </a>
          &ensp;&middot;&ensp;
          <ReportProblemLink />
        </p>
      </div>
    </div>
  );
}
